import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import ViewHeader from './ViewHeader';

export type StepStatus = 'pending' | 'in_progress' | 'done';
// What a step shows: the session step reads 'scheduled' (navy) once the CSM has scheduled it.
type DisplayStatus = StepStatus | 'scheduled';

export interface OnboardingViewProps {
  locations: StepStatus;
  onboarding: StepStatus;
  onboardingDate?: string;
  onboardingMeetingUrl?: string;
}

// Yellow/green as in OrderStatusBar, plus navy for a scheduled session.
const STATUS_COLORS: Record<DisplayStatus, string> = {
  pending: '#ccc',
  in_progress: '#f2c94c',
  done: '#3c6',
  scheduled: '#325882',
};

// Darker shades of the same colors, readable as text on the page background.
const STATUS_TEXT_COLORS: Record<DisplayStatus, string> = {
  pending: '#999',
  in_progress: '#b8860b',
  done: '#27a05a',
  scheduled: '#325882',
};

const OnboardingView = ({
  locations,
  onboarding,
  onboardingDate,
  onboardingMeetingUrl,
}: OnboardingViewProps) => {
  const { t, i18n } = useTranslation('onboarding');
  const meetingDate = onboardingDate ? new Date(onboardingDate) : undefined;
  // Dates follow the checklist's language, not the browser's, so they match the text around them.
  const lang = i18n.language;

  // filled: a solid marker with a white ✓ or number (done steps and a scheduled session); the rest are outlined.
  const steps: { title: string; status: DisplayStatus; filled: boolean; detail: string; extra?: React.ReactNode }[] = [
    {
      title: t('account.title'),
      status: 'done',
      filled: true,
      detail: t('account.detail'),
    },
    {
      // In progress until the CSM schedules the session with its Meet link (no per-location status).
      title: t(locations === 'in_progress' ? 'locations.titleInProgress' : 'locations.title'),
      status: locations,
      filled: locations === 'done',
      detail: t(`locations.${locations}`),
    },
    {
      title: t('session.title'),
      status: onboarding === 'in_progress' ? 'scheduled' : onboarding,
      filled: onboarding !== 'pending',
      // The CSM sets the status, date and link in HubSpot; any of the latter two can be missing.
      detail: t(`session.${onboarding === 'pending' ? 'unscheduled' : onboarding === 'done' ? 'done' : 'scheduled'}`),
      extra:
        (meetingDate || onboardingMeetingUrl) && onboarding !== 'done' ? (
          <div className="meeting">
            {meetingDate && (
              <div>
                <strong>
                  {meetingDate.toLocaleDateString(lang, { weekday: 'long', month: 'long', day: 'numeric' })}
                </strong>
                <small>
                  {meetingDate.toLocaleTimeString(lang, { hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' })}
                </small>
              </div>
            )}
            {onboardingMeetingUrl && (
              <a href={onboardingMeetingUrl} target="_blank" rel="noopener noreferrer">
                {t('session.join')}
              </a>
            )}
          </div>
        ) : null,
    },
  ];

  return (
    <StyledContainer>
      <ViewHeader>{t('title')}</ViewHeader>
      <p className="intro">{t('intro')}</p>
      <ol>
        {steps.map(({ title, status, filled, detail, extra }, i) => (
          <li key={title}>
            <StyledMarker
              style={{
                borderColor: STATUS_COLORS[status],
                background: filled ? STATUS_COLORS[status] : 'transparent',
                color: filled ? '#fff' : STATUS_TEXT_COLORS[status],
              }}
            >
              {status === 'done' ? '✓' : i + 1}
            </StyledMarker>
            <div>
              <span style={{ color: STATUS_TEXT_COLORS[status] }}>
                {t(`status.${status}`)}
              </span>
              <h3>{title}</h3>
              <p>{detail}</p>
              {extra}
            </div>
          </li>
        ))}
      </ol>
    </StyledContainer>
  );
};

export const StyledContainer = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 0 16px;
  color: #595959;

  h2 {
    margin-bottom: 8px;
  }

  .intro {
    margin: 0 0 48px;
    font-size: 16px;
  }

  ol {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  ol > li {
    position: relative;
    display: flex;
    gap: 24px;
    padding-bottom: 40px;
  }

  ol > li > div:last-child {
    flex: 1;
  }

  /* Thin rail connecting the markers. */
  ol > li:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 36px;
    bottom: 4px;
    width: 1px;
    background: #d9d9d9;
  }

  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  h3 {
    margin: 4px 0 6px;
    font-size: 18px;
    font-weight: 600;
    color: #325882;
  }

  li p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }

  /* Scheduled session summary under "Onboarding". */
  .meeting {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 14px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #e3e3e3;
    border-radius: 8px;
  }

  .meeting strong {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: #325882;
  }

  .meeting strong::first-letter {
    text-transform: uppercase;
  }

  .meeting small {
    font-size: 13px;
  }

  .meeting a {
    padding: 8px 16px;
    border-radius: 6px;
    background: #325882;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
  }

  .meeting a:hover {
    background: #274566;
  }
`;

const StyledMarker = styled.div`
  flex: none;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  border: 2px solid;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
`;

export default OnboardingView;
